import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? parseISO(date) : date;
  return format(d, "MMM d, yyyy");
}

export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return "—";
  return new Intl.NumberFormat().format(num);
}

export function formatPercent(num: number | null | undefined, decimals = 1): string {
  if (num === null || num === undefined) return "—";
  return `${num.toFixed(decimals)}%`;
}

export function truncate(str: string | null | undefined, length: number): string {
  if (!str) return "";
  if (str.length <= length) return str;
  return str.slice(0, length) + "...";
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

export function ensureHttps(url: string | null | undefined): string | null {
  if (!url) return null;
  let normalized = url.trim();
  if (!normalized) return null;
  if (!/^https?:\/\//i.test(normalized)) {
    normalized = `https://${normalized}`;
  }
  // Force www. on LinkedIn URLs to avoid bare-domain redirect that gets
  // blocked by COOP when navigating from inside an iframe (e.g. Lovable preview).
  try {
    const u = new URL(normalized);
    if (u.hostname === "linkedin.com") {
      u.hostname = "www.linkedin.com";
      normalized = u.toString();
    }
  } catch {
    // fall through with original normalized string
  }
  return normalized;
}

/**
 * Open an external URL in a new top-level tab.
 *
 * We synthesize a real `<a target="_blank" rel="noopener noreferrer">` and
 * click it instead of calling `window.open(...)`. When the app runs inside an
 * iframe (e.g. the Lovable preview or an embedded surface), a scripted
 * `window.open` popup inherits the iframe's restricted browsing context, so
 * sites that refuse to be embedded (LinkedIn) get blocked by Chrome with
 * `ERR_BLOCKED_BY_RESPONSE`. A genuine anchor activation with `target="_blank"`
 * breaks out to a true top-level browsing context, which is the most reliable
 * way past that block. `rel="noopener noreferrer"` keeps `window.opener` null.
 */
export function openExternal(url: string | null | undefined): void {
  const normalized = ensureHttps(url);
  if (!normalized) return;
  if (typeof document === "undefined") return;
  const a = document.createElement("a");
  a.href = normalized;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
